/**
 * Gantt VSM — Caminho Crítico por Etapa
 * Cada linha = uma atividade do longestPath; barra = TRA + Espera (TP − TRA).
 */

import React, { useMemo, useState } from 'react';
import { Clock, AlertTriangle, Zap, TrendingUp, DollarSign, Download } from 'lucide-react';
import { CardData } from '../../types';
import {
  buildCriticalPathGantt,
  formatDuration,
  CriticalPathGanttRow,
} from '../../utils/ganttTransform';

const COLORS = {
  VA: '#1D4ED8',
  VA_GRADIENT: 'linear-gradient(90deg, #2563eb 0%, #1e40af 100%)',
  NVA_PATTERN: 'repeating-linear-gradient(135deg, #f8fafc 0px, #f8fafc 5px, #eef2f7 5px, #eef2f7 10px)',
  NVA_SOLID: '#d97706',
  CRITICAL: '#dc2626',
  TAKT_LINE: '#ef4444',
};

interface GanttChartProps {
  cards: CardData[];
  /** IDs do caminho crítico (metrics.longestPathIds) */
  criticalPathIds?: string[];
  taktTime?: number;
  hourlyWaitCost?: number;
  maxHeight?: number;
}

interface TooltipData {
  visible: boolean;
  x: number;
  y: number;
  row: CriticalPathGanttRow | null;
  segment: 'work' | 'wait';
}

interface HoveredRowData {
  rowId: string;
  startMinutes: number;
  totalDuration: number;
}

export const GanttChart: React.FC<GanttChartProps> = ({
  cards,
  criticalPathIds,
  taktTime = 12,
  hourlyWaitCost = 150,
  maxHeight = 320,
}) => {
  const chartAreaRef = React.useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<TooltipData>({
    visible: false,
    x: 0,
    y: 0,
    row: null,
    segment: 'work',
  });
  const [hoveredRow, setHoveredRow] = useState<HoveredRowData | null>(null);

  const ganttData = useMemo(
    () => buildCriticalPathGantt(cards, criticalPathIds),
    [cards, criticalPathIds],
  );

  const { rows, totalDuration, stats, zoom } = ganttData;

  const realPCE = useMemo(() => {
    if (totalDuration <= 0) return 0;
    const pct = (stats.totalWorkTime / totalDuration) * 100;
    return Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0;
  }, [stats.totalWorkTime, totalDuration]);

  const timeMarks = useMemo(() => {
    const marks: number[] = [];
    const interval = zoom.tickInterval;
    for (let t = 0; t <= totalDuration; t += interval) {
      marks.push(t);
    }
    return marks;
  }, [totalDuration, zoom.tickInterval]);

  const showTooltip = (
    e: React.MouseEvent,
    row: CriticalPathGanttRow,
    segment: 'work' | 'wait',
  ) => {
    const chartRect = chartAreaRef.current?.getBoundingClientRect();
    const fallbackRect = e.currentTarget.getBoundingClientRect();
    const rect = chartRect ?? fallbackRect;
    const tooltipWidth = 340;
    setTooltip({
      visible: true,
      x: Math.min(
        Math.max(e.clientX - rect.left + 12, 8),
        Math.max(8, rect.width - tooltipWidth - 8),
      ),
      y: Math.max(20, e.clientY - rect.top - 10),
      row,
      segment,
    });
  };

  const hideTooltip = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const calculateWaitCost = (waitMinutes: number): string => {
    const cost = (waitMinutes / 60) * hourlyWaitCost;
    return cost.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatMinutesShort = (value: number) => `${Math.max(0, Math.round(value))}m`;

  if (rows.length === 0 || totalDuration <= 0) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400 dark:text-slate-500 text-sm">
        <Clock className="mr-2" size={16} />
        Adicione atividades ao mapa para visualizar o caminho crítico
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col font-sans">
      <div className="shrink-0 flex items-center justify-between mb-4 px-3 py-2.5 bg-slate-100/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded">
        <div className="flex items-center gap-5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">
          <span className="flex items-center gap-2">
            <div className="w-4 h-3 rounded-sm shadow-sm" style={{ background: COLORS.VA_GRADIENT }} />
            Trabalho (TRA)
          </span>
          <span className="flex items-center gap-2">
            <div
              className="w-4 h-3 rounded-sm border border-dashed border-slate-400 dark:border-slate-500"
              style={{ background: COLORS.NVA_PATTERN }}
            />
            Espera (TP − TRA)
          </span>
          <span className="flex items-center gap-2">
            <div
              className="w-4 h-3 rounded-sm"
              style={{
                background: COLORS.VA,
                border: `2px solid ${COLORS.CRITICAL}`,
                boxShadow: `0 0 4px ${COLORS.CRITICAL}`,
              }}
            />
            Caminho Crítico
          </span>
        </div>

        <div className="flex items-center gap-4 text-[11px] text-slate-600 dark:text-slate-400">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold rounded border border-slate-300 dark:border-slate-600 bg-white/80 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Download size={11} strokeWidth={1.5} className="text-slate-500 dark:text-slate-400" />
            Exportar PDF
          </button>
          <span className="flex items-center gap-1.5">
            <Clock size={13} className="text-slate-400" />
            Lead Time:
            <b className="text-slate-800 dark:text-slate-200 ml-0.5">{formatDuration(totalDuration)}</b>
          </span>
          <span className="flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-white dark:bg-slate-700 shadow-sm">
            <Zap size={13} className={realPCE >= 25 ? 'text-emerald-500' : 'text-amber-500'} />
            PCE:
            <b className={`ml-0.5 ${realPCE >= 25 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
              {realPCE.toFixed(1)}%
            </b>
          </span>
          <span className="flex items-center gap-1.5">
            <TrendingUp size={13} className="text-red-400" />
            Etapas:
            <b className="text-red-600 dark:text-red-400 ml-0.5">{stats.activityCount}</b>
          </span>
        </div>
      </div>

      <div
        ref={chartAreaRef}
        className="flex-1 min-h-0 relative overflow-x-hidden overflow-y-auto"
        style={{ minHeight: Math.min(maxHeight, 260) }}
      >
        <div className="w-full min-h-full">
          <div className="flex items-end h-7 border-b border-slate-300 dark:border-slate-700 mb-2">
            <div className="w-44 shrink-0" />
            <div className="flex-1 relative">
              {timeMarks.map((time, idx) => (
                <div
                  key={idx}
                  className="absolute bottom-0 flex flex-col items-center"
                  style={{ left: `${(time / totalDuration) * 100}%`, transform: 'translateX(-50%)' }}
                >
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 font-medium mb-1">
                    {formatDuration(time)}
                  </span>
                  <div className="w-px h-2 bg-slate-300 dark:bg-slate-600" />
                </div>
              ))}
            </div>
            <div className="w-14 shrink-0 bg-slate-50/50 dark:bg-slate-800/40 border-l border-slate-200/70 dark:border-slate-700 text-right text-[10px] font-semibold text-slate-500 dark:text-slate-400 pr-1.5">
              TOTAL
            </div>
          </div>

          {rows.map(row => {
            const leftPercent = (row.startMinutes / totalDuration) * 100;
            const totalWidthPercent = (row.totalDuration / totalDuration) * 100;
            const workWidthPercent =
              row.totalDuration > 0 ? (row.workDuration / row.totalDuration) * 100 : 0;
            const waitWidthPercent =
              row.totalDuration > 0 ? (row.waitDuration / row.totalDuration) * 100 : 0;
            const isHovered = hoveredRow?.rowId === row.id;
            const hoveredTaktWidthPercent =
              row.totalDuration > 0 && taktTime > 0
                ? Math.min(100, (taktTime / row.totalDuration) * 100)
                : 0;

            return (
              <div key={row.id} className="flex items-center mb-1.5">
                <div className="w-44 shrink-0 pr-2 pl-1 text-left">
                  <div
                    className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 truncate leading-tight"
                    title={row.label}
                  >
                    {row.label}
                  </div>
                </div>

                <div className="flex-1 relative h-6 bg-white dark:bg-[var(--bg-surface)] border border-slate-200 dark:border-slate-700 rounded-sm overflow-visible">
                  {timeMarks.map((time, idx) => (
                    <div
                      key={idx}
                      className="absolute top-0 bottom-0 border-l border-slate-100 dark:border-slate-800"
                      style={{ left: `${(time / totalDuration) * 100}%` }}
                    />
                  ))}

                  <div
                    className="absolute top-0 bottom-0 flex rounded-sm overflow-hidden"
                    style={{
                      left: `${leftPercent}%`,
                      width: `${totalWidthPercent}%`,
                      minWidth: '6px',
                      border: `1px solid ${COLORS.CRITICAL}`,
                      boxShadow: `0 0 3px ${COLORS.CRITICAL}55`,
                    }}
                    onMouseEnter={() => {
                      setHoveredRow({
                        rowId: row.id,
                        startMinutes: row.startMinutes,
                        totalDuration: row.totalDuration,
                      });
                    }}
                    onMouseLeave={() => {
                      setHoveredRow(prev => (prev?.rowId === row.id ? null : prev));
                      hideTooltip();
                    }}
                  >
                    {isHovered && hoveredTaktWidthPercent > 0 && (
                      <div
                        className="absolute inset-y-0 left-0 pointer-events-none z-[1]"
                        style={{
                          width: `${hoveredTaktWidthPercent}%`,
                          background:
                            'linear-gradient(90deg, rgba(239, 68, 68, 0.14) 0%, rgba(239, 68, 68, 0.08) 100%)',
                          borderLeft: '2px solid #ef4444',
                        }}
                      />
                    )}

                    {row.workDuration > 0 && (
                      <div
                        className="relative z-[3] h-full flex items-center justify-center hover:brightness-95 transition-all shadow-sm flex-shrink-0"
                        style={{
                          width: `${workWidthPercent}%`,
                          minWidth: row.workDuration > 0 ? '4px' : '0px',
                          background: '#2563EB',
                        }}
                        onMouseEnter={e => showTooltip(e, row, 'work')}
                        title={`TRA: ${formatMinutesShort(row.workDuration)}`}
                      >
                        {workWidthPercent > 15 && (
                          <span className="text-[9px] font-bold text-white tabular-nums">
                            {formatMinutesShort(row.workDuration)}
                          </span>
                        )}
                      </div>
                    )}

                    {row.waitDuration > 0 && (
                      <div
                        className="relative z-[2] h-full flex items-center justify-center hover:brightness-95 transition-all border-l border-dashed border-slate-300 dark:border-slate-500"
                        style={{
                          width: `${waitWidthPercent}%`,
                          minWidth: row.waitDuration > 0 ? '2px' : '0px',
                          background: COLORS.NVA_PATTERN,
                        }}
                        onMouseEnter={e => showTooltip(e, row, 'wait')}
                        title={`Espera: ${formatMinutesShort(row.waitDuration)}`}
                      >
                        {waitWidthPercent > 15 && (
                          <span className="text-[9px] font-semibold text-slate-500 dark:text-slate-400 tabular-nums">
                            {formatMinutesShort(row.waitDuration)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="w-14 shrink-0 pl-2 pr-1.5 text-right bg-slate-50/50 dark:bg-slate-800/40 border-l border-slate-200/70 dark:border-slate-700">
                  <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200 tabular-nums">
                    {formatMinutesShort(row.totalDuration)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {tooltip.visible && tooltip.row && (
          <div
            className="absolute z-50 bg-slate-800 text-white text-[11px] rounded-md shadow-xl p-2.5 pointer-events-none border border-slate-700"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translateY(-110%)',
              minWidth: '320px',
            }}
          >
            <div className="font-bold text-[11px] mb-1.5 pb-1 border-b border-slate-700">
              {tooltip.row.label}
            </div>
            <div className="text-slate-100 font-medium">
              {tooltip.segment === 'work'
                ? `TRA (trabalho): ${formatMinutesShort(tooltip.row.workDuration)}`
                : `Espera (TP − TRA): ${formatMinutesShort(tooltip.row.waitDuration)}`}
              {' | '}
              TP total: {formatMinutesShort(tooltip.row.totalDuration)}
              {' | '}
              Eficiência da etapa: {efficiencyLabel(tooltip.row)}%
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 mt-4 pt-3 pb-6 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-[11px] text-slate-600 dark:text-slate-400 px-3">
        <div className="flex items-center gap-5">
          <span className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.VA }} />
            <b className="text-blue-600 dark:text-blue-400">{formatDuration(stats.totalWorkTime)}</b>
            <span className="text-slate-400">TRA</span>
          </span>
          <span className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS.NVA_SOLID }} />
            <b className="text-amber-600 dark:text-amber-400">{formatDuration(stats.totalWaitTime)}</b>
            <span className="text-slate-400">espera</span>
          </span>
          <span className="flex items-center gap-1.5 text-red-500">
            <DollarSign size={12} />
            Custo NVA: <b>{calculateWaitCost(stats.totalWaitTime)}</b>
          </span>
          <span className="text-slate-500">
            PCE = <b className="text-slate-700 dark:text-slate-200">Σ TRA / Σ TP</b>
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 dark:bg-red-900/20">
            <AlertTriangle size={11} className="text-red-500" />
            <b className="text-red-600 dark:text-red-400">{stats.activityCount}</b>
            <span className="text-red-500">etapas no caminho crítico</span>
          </span>
        </div>
      </div>
    </div>
  );
};

function efficiencyLabel(row: CriticalPathGanttRow): string {
  if (row.totalDuration <= 0) return '0.0';
  return ((row.workDuration / row.totalDuration) * 100).toFixed(1);
}
